/**
 * Account expiry rule — docs/designs/account-expiry-dates.md.
 *
 * A user's access ends on the EARLIER of their own `users.expires_at` and their
 * company's `companies.expires_at`. Both are DATE columns: the date itself is
 * the first day without access, so an account is expired once local time has
 * reached 00:00 of that day.
 *
 * Pure and browser-safe: used by the auth routes (server) and AdminPanel (client).
 */

/** What a DATE column looks like by the time it reaches us: `pg` hands back a
 *  `Date` at local midnight, `to_char(..., 'YYYY-MM-DD')` and form inputs hand
 *  back a string. */
export type ExpiryValue = Date | string | null | undefined;

export const ACCOUNT_EXPIRED_CODE = 'ACCOUNT_EXPIRED';
export const ACCOUNT_EXPIRED_MESSAGE =
  'Your account has been disabled because its access period has ended. Please contact an administrator.';

/** sessionStorage key AuthProvider uses to hand a notice to the login page. */
export const AUTH_NOTICE_STORAGE_KEY = 'authNotice';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Accepts `YYYY-MM-DD` (a real calendar date) or an empty value. Used to validate
 * the `expiresAt` field on admin requests before it reaches SQL.
 */
export function isValidExpiryInput(value: unknown): value is string | null | undefined {
  if (value === undefined || value === null || value === '') return true;
  if (typeof value !== 'string') return false;
  return toExpiryDate(value) !== null;
}

/**
 * Normalise an expiry value to a `Date` at local midnight, or `null` when unset
 * or unparseable. Strings are parsed by hand so `YYYY-MM-DD` means *local*
 * midnight, matching what `pg` does for DATE columns (`new Date('YYYY-MM-DD')`
 * would be UTC midnight and shift by the timezone offset).
 */
export function toExpiryDate(value: ExpiryValue): Date | null {
  if (value === undefined || value === null || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const match = DATE_ONLY.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  // Reject overflowed dates such as 2026-02-31 (which Date would roll forward).
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/** Format a Date as `YYYY-MM-DD` in local time (the shape `<input type="date">` wants). */
export function formatExpiryDate(date: Date): string {
  const y = String(date.getFullYear()).padStart(4, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * The binding expiry for a user: the earlier of their own date and their
 * company's date, or `null` when neither is set.
 */
export function effectiveExpiry(userExpiresAt: ExpiryValue, companyExpiresAt: ExpiryValue) {
  const user = toExpiryDate(userExpiresAt);
  const company = toExpiryDate(companyExpiresAt);
  if (user && company) {
    return {
      date: user <= company ? user : company,
      source: user <= company ? 'user' : 'company'
    } as const;
  }
  if (user) return { date: user, source: 'user' } as const;
  if (company) return { date: company, source: 'company' } as const;
  return null;
}

/**
 * True once `now` has reached the start of the effective expiry day.
 */
export function isAccountExpired(
  userExpiresAt: ExpiryValue,
  companyExpiresAt: ExpiryValue,
  now: Date = new Date()
): boolean {
  const effective = effectiveExpiry(userExpiresAt, companyExpiresAt);
  if (!effective) return false;
  return now.getTime() >= effective.date.getTime();
}
